import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/auth";
import { Spinner } from "@/components/ui";
import { AppShell } from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import BookingsListPage from "@/pages/BookingsListPage";
import BookingDetailPage from "@/pages/BookingDetailPage";
import QuickCreateBookingPage from "@/pages/QuickCreateBookingPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import OperationsPage from "@/pages/OperationsPage";
import FinancePage from "@/pages/FinancePage";
import AgentsPage from "@/pages/AgentsPage";
import AuditLogPage from "@/pages/AuditLogPage";

// Heavier / less-used routes are code-split.
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PackagesListPage = lazy(() => import("@/pages/PackagesListPage"));
const PackageDetailPage = lazy(() => import("@/pages/PackageDetailPage"));
const ContentPage = lazy(() => import("@/pages/ContentPage"));
const ContentDetailPage = lazy(() => import("@/pages/ContentDetailPage"));
const DispatchPage = lazy(() => import("@/pages/DispatchPage"));
const DispatchAgentPage = lazy(() => import("@/pages/DispatchAgentPage"));
const DispatchRunPage = lazy(() => import("@/pages/DispatchRunPage"));
const KnowledgePage = lazy(() => import("@/pages/KnowledgePage"));
const KnowledgeDocPage = lazy(() => import("@/pages/KnowledgeDocPage"));
const MediaPage = lazy(() => import("@/pages/MediaPage"));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

function Protected({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AppShell>
        <Suspense fallback={<Spinner />}>{children}</Suspense>
      </AppShell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Protected><HomePage /></Protected>} />
      <Route path="/bookings" element={<Protected><BookingsListPage /></Protected>} />
      <Route path="/bookings/quick" element={<Protected><QuickCreateBookingPage /></Protected>} />
      <Route path="/bookings/:ref" element={<Protected><BookingDetailPage /></Protected>} />
      <Route path="/leads" element={<Protected><LeadsPage /></Protected>} />
      <Route path="/leads/:id" element={<Protected><LeadDetailPage /></Protected>} />
      <Route path="/customers" element={<Protected><CustomersPage /></Protected>} />
      <Route path="/customers/:id" element={<Protected><CustomerDetailPage /></Protected>} />
      <Route path="/packages" element={<Protected><PackagesListPage /></Protected>} />
      <Route path="/packages/:ref" element={<Protected><PackageDetailPage /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/operations" element={<Protected><OperationsPage /></Protected>} />
      <Route path="/finance" element={<Protected><FinancePage /></Protected>} />
      <Route path="/agents" element={<Protected><AgentsPage /></Protected>} />
      <Route path="/content" element={<Protected><ContentPage /></Protected>} />
      <Route path="/content/:id" element={<Protected><ContentDetailPage /></Protected>} />
      <Route path="/dispatch" element={<Protected><DispatchPage /></Protected>} />
      <Route path="/dispatch/agents/:key" element={<Protected><DispatchAgentPage /></Protected>} />
      <Route path="/dispatch/runs/:id" element={<Protected><DispatchRunPage /></Protected>} />
      <Route path="/knowledge" element={<Protected><KnowledgePage /></Protected>} />
      <Route path="/knowledge/:id" element={<Protected><KnowledgeDocPage /></Protected>} />
      <Route path="/media" element={<Protected><MediaPage /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLogPage /></Protected>} />
      <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
